// Modal de envio de contato (vCard) pro cliente. 2 abas:
//   - Buscar: uma lista so, fundindo clientes cadastrados (/api/clientes) com os
//     contatos do WhatsApp (/api/contatos, sem grupos). O operador pensa por NOME,
//     nao por qual cadastro guarda o numero — separar os dois em abas escondia
//     contato que existe (fornecedor so no WhatsApp nunca aparecia na aba Clientes).
//     Mesmo numero nos dois lados = 1 linha (dedup por DDD+8 digitos).
//     De campo VAZIO a lista mostra as CONVERSAS RECENTES (quem falou com a loja
//     por ultimo), nao os primeiros contatos do cadastro — o contato que se
//     encaminha e quase sempre o de agora, e a lista crua so despejava numeros
//     sem nome. Digitou, volta a valer a busca nos dois cadastros.
//   - Novo: digita nome + telefone na hora
//
// Backend: POST /api/conversas/:id/mensagens/contato { contato_nome, contato_telefone }
// Funciona tanto em Cloud (atendimento) quanto Baileys (compras) — services tem sendContact.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Loader2, UserPlus } from 'lucide-react';
import { parseSPDate } from '@fiodecobre/shared';
import { atendimentoApi } from '../lib/api';
import { useListaTeclado } from '../../../lib/useListaTeclado';
import { Avatar } from '../../../components/Avatar';
import type { Contato, ClienteResumo, Conversa } from '../lib/tipos';

interface Props {
  conversaId: number;
  onClose: () => void;
  onEnviado?: () => void;
}

type Aba = 'buscar' | 'novo';

interface Selecionavel {
  nome: string;
  telefone: string;
  legenda?: string;
  fotoUrl?: string | null;
  cliente: boolean;
}

function digitsOnly(s: string | null | undefined): string {
  return String(s ?? '').replace(/\D/g, '');
}

// Chave de dedup: DDD + ultimos 8 digitos. Casa o mesmo numero com/sem DDI 55 e
// com/sem o 9o digito do celular (cliente cadastrado "64 9240-5396" x jid "5564992405396").
function chaveTel(raw: string): string {
  const d = digitsOnly(raw);
  const semDdi = d.startsWith('55') && (d.length === 12 || d.length === 13) ? d.slice(2) : d;
  return semDdi.length >= 10 ? semDdi.slice(0, 2) + semDdi.slice(-8) : semDdi;
}

function clienteParaSelecionavel(c: ClienteResumo): Selecionavel | null {
  const tel = digitsOnly(c.whatsapp) || digitsOnly(c.telefone);
  if (!tel) return null;
  return {
    nome: c.nome,
    telefone: tel,
    legenda: [c.codigo_legado ? `Cod ${c.codigo_legado}` : null, c.cidade && c.uf ? `${c.cidade}/${c.uf}` : null]
      .filter(Boolean).join(' · '),
    fotoUrl: null,
    cliente: true,
  };
}

// Nome so de pontuacao (".", "...") nao identifica ninguem — cai pro proximo candidato.
function primeiroNomeUtil(...cands: Array<string | null | undefined>): string | null {
  for (const c of cands) {
    const v = String(c ?? '').trim();
    if (v && /[\p{L}\p{N}]/u.test(v)) return v;
  }
  return null;
}

function contatoParaSelecionavel(c: Contato): Selecionavel | null {
  const tel = digitsOnly(c.telefone) || digitsOnly(c.jid);
  if (!tel) return null;
  const nome = primeiroNomeUtil(c.nome, c.push_name, c.cliente_nome) ?? tel;
  const apelido = primeiroNomeUtil(c.push_name);
  return {
    nome,
    telefone: tel,
    legenda: apelido && apelido.toUpperCase() !== nome.toUpperCase() ? apelido : undefined,
    fotoUrl: c.foto_url,
    cliente: !!c.cliente_id,
  };
}

export function ModalEnviarContato({ conversaId, onClose, onEnviado }: Props) {
  const [aba, setAba] = useState<Aba>('buscar');
  const [busca, setBusca] = useState('');
  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [recentes, setRecentes] = useState<Conversa[]>([]);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Form da aba "Novo"
  const [novoNome, setNovoNome] = useState('');
  const [novoTelefone, setNovoTelefone] = useState('');

  const inputRef = useRef<HTMLInputElement | null>(null);

  const temBusca = busca.trim().length > 0;

  // Campo vazio = conversas recentes, na ordem em que falaram (mais nova primeiro).
  // Sem ordenar por nome: aqui a ordem E a informacao. Grupo fora (nao se manda vCard
  // de grupo) e a propria conversa aberta fora (mandar o contato dele pra ele e ruido).
  //
  // `ultima_mensagem_em` nulo fora TAMBEM: sao canais abertos por "Nova conversa" que
  // nunca trocaram mensagem (35 so na inbox compras). O `ORDER BY ... DESC` do Postgres
  // e NULLS FIRST, entao eles chegam no TOPO da resposta — pedir os 40 primeiros trazia
  // so eles, e nenhuma conversa de verdade. Mesma regra que a lista lateral ja aplica ao
  // reordenar (`new Date(x || 0)` joga nulo pro fim).
  const listaRecentes: Selecionavel[] = useMemo(() => {
    const porChave = new Map<string, Selecionavel>();
    const ordenadas = recentes
      .filter((cv) => cv.ultima_mensagem_em != null)
      .sort((a, b) => (parseSPDate(b.ultima_mensagem_em)?.getTime() ?? 0) - (parseSPDate(a.ultima_mensagem_em)?.getTime() ?? 0));
    for (const cv of ordenadas) {
      if (cv.id === conversaId || cv.contato?.grupo) continue;
      const s = contatoParaSelecionavel(cv.contato);
      if (!s) continue;
      const k = chaveTel(s.telefone);
      if (!porChave.has(k)) porChave.set(k, s);
      if (porChave.size >= 30) break;
    }
    return [...porChave.values()];
  }, [recentes, conversaId]);

  // Lista unica: clientes + contatos do WhatsApp, mesmo numero fundido numa linha so.
  const listaBusca: Selecionavel[] = useMemo(() => {
    const porChave = new Map<string, Selecionavel>();
    for (const c of clientes) {
      const s = clienteParaSelecionavel(c);
      if (!s) continue;
      const k = chaveTel(s.telefone);
      if (!porChave.has(k)) porChave.set(k, s);
    }
    for (const c of contatos) {
      const s = contatoParaSelecionavel(c);
      if (!s) continue;
      const k = chaveTel(s.telefone);
      const ja = porChave.get(k);
      if (!ja) { porChave.set(k, s); continue; }
      // Ja veio pelo cadastro de clientes: herda a foto do WhatsApp, o numero do jid
      // (e o verificado, que entrega) e o nome do WhatsApp na legenda quando difere.
      if (!ja.fotoUrl) ja.fotoUrl = s.fotoUrl;
      ja.telefone = s.telefone;
      if (s.nome.toUpperCase() !== ja.nome.toUpperCase()) {
        ja.legenda = [ja.legenda, s.nome].filter(Boolean).join(' · ');
      }
    }
    return [...porChave.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [clientes, contatos]);

  const lista = temBusca ? listaBusca : listaRecentes;

  const teclado = useListaTeclado(lista, (item) => enviar(item));

  useEffect(() => { inputRef.current?.focus(); }, [aba]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enviando) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, enviando]);

  // Reset busca ao trocar de aba
  useEffect(() => { setBusca(''); setErro(null); }, [aba]);

  // Debounce + fetch dos dois cadastros em paralelo. Sem busca nao consulta cadastro
  // nenhum: mostra as conversas recentes (uma chamada so, a mesma da lista lateral).
  useEffect(() => {
    if (aba === 'novo') return;
    setLoading(true);
    const q = busca.trim();
    let vivo = true;
    const timer = setTimeout(() => {
      if (!q) {
        atendimentoApi.listarConversas({})
          .catch(() => [] as Conversa[])
          .then((cvs) => { if (vivo) setRecentes(cvs); })
          .finally(() => { if (vivo) setLoading(false); });
        return;
      }
      const pClientes = atendimentoApi.buscarClientes(q).catch(() => [] as ClienteResumo[]);
      const pContatos = atendimentoApi.listarContatos({ busca: q, sem_grupo: true, limit: 50 })
        .catch(() => [] as Contato[]);
      Promise.all([pClientes, pContatos])
        .then(([cs, ws]) => { if (!vivo) return; setClientes(cs); setContatos(ws); })
        .finally(() => { if (vivo) setLoading(false); });
    }, q ? 250 : 0);
    return () => { vivo = false; clearTimeout(timer); };
  }, [busca, aba]);

  async function enviar(item: Selecionavel) {
    if (enviando) return;
    const nome = item.nome.trim();
    const tel = digitsOnly(item.telefone);
    if (!nome || !tel) { setErro('Nome e telefone obrigatorios.'); return; }
    setEnviando(true);
    setErro(null);
    try {
      await atendimentoApi.enviarContato(conversaId, { contato_nome: nome, contato_telefone: tel });
      onEnviado?.();
      onClose();
    } catch (e: any) {
      setErro(e?.message || 'Falha ao enviar contato');
      setEnviando(false);
    }
  }

  function enviarNovo() {
    enviar({ nome: novoNome, telefone: novoTelefone, cliente: false });
  }

  function fmtTel(d: string): string {
    if (!d) return '';
    if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
    if (d.length === 12) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return d;
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-2" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="modal-conteudo rounded-lg shadow-xl overflow-hidden flex flex-col"
        style={{ backgroundColor: 'var(--modal-bg, #fff)', width: '100%', maxWidth: 560, maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="modal-header flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center gap-2">
            <UserPlus size={18} />
            <h3 className="font-semibold">Enviar contato</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={enviando}
            className="btn-fechar-modal p-1 rounded hover:bg-black/5"
            title="Fechar (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        {/* Abas */}
        <div className="flex border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          {([
            { id: 'buscar' as Aba, label: 'Buscar', icon: Search },
            { id: 'novo' as Aba, label: 'Novo', icon: UserPlus },
          ]).map(({ id, label, icon: Icone }) => (
            <button
              key={id}
              type="button"
              onClick={() => setAba(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition ${aba === id ? 'border-b-2 border-blue-500 text-blue-700' : 'text-gray-500 hover:bg-black/5'}`}
            >
              <Icone size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Conteudo da aba */}
        {aba === 'novo' ? (
          <div className="p-4 flex flex-col gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nome</label>
              <input
                ref={inputRef}
                type="text"
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                placeholder="Nome do contato"
                className="w-full px-3 py-2 border rounded-lg outline-none"
                style={{ fontSize: '16px' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Telefone</label>
              <input
                type="tel"
                inputMode="tel"
                value={novoTelefone}
                onChange={(e) => setNovoTelefone(e.target.value)}
                placeholder="Ex: 64 9 9999-9999"
                className="no-upper w-full px-3 py-2 border rounded-lg outline-none"
                style={{ fontSize: '16px', textTransform: 'none' }}
              />
              <p className="text-xs text-gray-500 mt-1">Pode digitar com ou sem DDI. So os digitos contam.</p>
            </div>
            <button
              type="button"
              onClick={enviarNovo}
              disabled={enviando || !novoNome.trim() || digitsOnly(novoTelefone).length < 8}
              className="self-end px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {enviando ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              Enviar contato
            </button>
          </div>
        ) : (
          <>
            <div className="px-3 pt-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  ref={inputRef}
                  type="search"
                  placeholder="Buscar por nome, telefone ou codigo..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  onKeyDown={teclado.onKeyDown}
                  className="no-upper w-full pl-9 pr-3 py-2 border rounded-lg outline-none"
                  style={{ fontSize: '16px', textTransform: 'none' }}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pt-2 pb-3" style={{ minHeight: 200 }}>
              {loading ? (
                <div className="text-center text-gray-500 py-8 flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Carregando...
                </div>
              ) : lista.length === 0 ? (
                <div className="text-center text-gray-500 py-8 text-sm">
                  {temBusca
                    ? `Nenhum contato ou cliente pra "${busca}".`
                    : 'Nenhuma conversa recente. Digite pra buscar entre os clientes e os contatos do WhatsApp.'}
                </div>
              ) : (
                <>
                {!temBusca && (
                  <p className="px-2 pb-1 text-xs font-semibold text-gray-500 uppercase">Conversas recentes</p>
                )}
                <ul ref={teclado.listaRef as any} className="divide-y divide-gray-100">
                  {lista.map((item, idx) => {
                    const destaque = idx === teclado.highlight;
                    return (
                      <li key={`${item.telefone}-${idx}`} data-idx={idx}>
                        <button
                          type="button"
                          onClick={() => enviar(item)}
                          onMouseEnter={() => teclado.setHighlight(idx)}
                          disabled={enviando}
                          className={`w-full flex items-center gap-3 py-2 px-2 text-left disabled:opacity-60 rounded ${destaque ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <Avatar nome={item.nome} fotoUrl={item.fotoUrl ?? null} tamanho={36} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium truncate">{item.nome}</p>
                              {item.cliente && (
                                <span className="text-[10px] px-1 rounded flex-shrink-0" style={{ backgroundColor: '#fde68a', color: '#000' }}>Cliente</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {fmtTel(item.telefone)}{item.legenda ? ` · ${item.legenda}` : ''}
                            </p>
                          </div>
                          {enviando ? <Loader2 size={16} className="animate-spin text-gray-400" /> : <UserPlus size={16} className="text-gray-400" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                </>
              )}
            </div>
          </>
        )}

        {erro && (
          <div className="px-4 py-2 text-sm text-red-600 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            {erro}
          </div>
        )}
      </div>
    </div>
  );
}
